import {
  ArtifactViewProvider,
  type ArtifactViewPatcherOverride,
} from "@/components/artifacts/view/ArtifactViewContext.tsx";
import { renderComponent } from "@/components/artifacts/view/renderComponent.tsx";
import { cn } from "@/lib/utils.ts";
import type { ArtifactView as ArtifactViewSchema } from "@/types/artifacts-view.ts";

type ArtifactViewProps = {
  view: ArtifactViewSchema;
  threadId: number;
  messageId: number;
  artifactId: string;
  isReadOnly: boolean;
  /** Shared (unauthenticated) thread view — suppresses session-auth content. */
  isSharedThread?: boolean;
  /** Frame source for an applet the viewer does not own; puts it in read-only
   * mode. See `ArtifactViewCtx.appletPreviewSrc`. */
  appletPreviewSrc?: string;
  /** Full-size (lightbox) render — opts blocks into higher-res assets. */
  maximized?: boolean;
  /** Fill the host's height, for a host that does not scroll the view. */
  fill?: boolean;
  /** Close the surface hosting the view (the lightbox). Passed through to the
   * view context so blocks can dismiss the card after a terminal action. */
  onRequestClose?: () => void;
  /** Override the default network-based patcher. Used by the admin preview
   * tool to apply edits to local state instead of POSTing. */
  patcherOverride?: ArtifactViewPatcherOverride;
};

export const ArtifactView = ({
  view,
  threadId,
  messageId,
  artifactId,
  isReadOnly,
  isSharedThread,
  appletPreviewSrc,
  maximized,
  fill,
  onRequestClose,
  patcherOverride,
}: ArtifactViewProps) => {
  return (
    <ArtifactViewProvider
      threadId={threadId}
      messageId={messageId}
      artifactId={artifactId}
      isReadOnly={isReadOnly}
      isSharedThread={isSharedThread}
      appletPreviewSrc={appletPreviewSrc}
      maximized={maximized}
      fill={fill}
      onRequestClose={onRequestClose}
      patcherOverride={patcherOverride}
    >
      {/* ArtifactView owns its padding (px-5 py-4) so full-bleed components can
          reliably cancel it. `fullBleed` → `-mx-5` (edge-to-edge horizontally);
          when it's also the first component, `-mt-4` makes it flush to the top.
          Keep this padding in sync with the `-mx-5/-mt-4` offsets. */}
      <div
        className={cn(
          "flex flex-col px-5 py-4",
          view.spacing === "compact" ? "gap-2" : "gap-3",
          fill && "min-h-0 flex-1",
        )}
      >
        {view.components.map((component, index) => {
          const prev = view.components[index - 1];
          // A section label is a heading for the component that follows it, so
          // pull that component up to sit close beneath its label rather than a
          // full inter-component gap away.
          const hugsLabel = prev?.type === "sectionLabel";
          return (
            <div
              key={component.id}
              className={cn(
                fill && "min-h-0 flex-1",
                component.fullBleed && "-mx-5",
                component.fullBleed && index === 0 && "-mt-4",
                hugsLabel && "-mt-1",
              )}
            >
              {renderComponent(component)}
            </div>
          );
        })}
      </div>
    </ArtifactViewProvider>
  );
};
